import moment from "../../../moment";

/**
 * Checks whether a given user (row) is registered as a participant in any
 * uncompleted course session for the specified training.
 *
 * @param {object} row - User row object containing an `id` field.
 * @param {object} training - Training object with `uncompleted_courses` array.
 * @returns {boolean} True if the user appears in at least one uncompleted course.
 */
const isUserPlannedForTraining = (row, training) => {
  const uncompletedCourses = training.uncompleted_courses;
  const participants =
    uncompletedCourses.length > 0
      ? uncompletedCourses.flatMap((item) => item.participants).map((item) => item.id)
      : null;

  if (participants) {
    const isUserInParticipants = participants.filter((item) => item === row.id);
    return !!isUserInParticipants.length;
  }
  return false;
};

/**
 * Checks whether a user is currently enrolled in a planned course that covers
 * the given qualification's training. Looks across all of the user's existing
 * qualifications and their uncompleted course sessions.
 *
 * @param {object} row - User row containing a `qualifications` array and an `id`.
 * @param {object} qualification - The qualification record being evaluated.
 * @returns {boolean} True if the user is enrolled in a relevant planned course.
 */
const isUserPlannedFor = (row, qualification) => {
  if (row.qualifications.length) {
    const courses = row.qualifications
      .flatMap((item) => {
        if (item.id) {
          return item.training.uncompleted_courses;
        }
        return null;
      })
      .filter((item) => item !== null);

    const isInUncompletedCourses = courses.filter((item) => item.training_id === qualification.training_id);

    const participants =
      isInUncompletedCourses.length > 0
        ? isInUncompletedCourses.flatMap((item) => item.participants).map((item) => item.id)
        : [];

    const isUserInParticipants = participants.filter((item) => item === row.id);

    return !!isUserInParticipants.length && !!isInUncompletedCourses.length;
  }

  return false;
};

/**
 * Determines whether a qualification should display the "planned" badge.
 * A qualification is considered "planned" when:
 *  - The user is enrolled in a planned course for that training, AND
 *  - The qualification either has already expired OR is within its notification window.
 *
 * @param {string} expiryDate - ISO string of the qualification's expiry date.
 * @param {object} qualification - The qualification record (must have `training.notification_period`).
 * @param {object} row - User row (forwarded to `isUserPlannedFor`).
 * @returns {boolean} True if the planned badge should be shown.
 */
const isPlanned = (expiryDate, qualification, row) => {
  if (isUserPlannedFor(row, qualification) && qualification.id) {
    const diff = moment(expiryDate).diff(moment(), "milliseconds");
    const notificationPeriodInMs = moment.duration(qualification.training.notification_period, "day").asMilliseconds();

    // Qualification has already expired
    const isExpired = diff <= 0;
    const isExpiringSoon = diff > 0 && diff <= notificationPeriodInMs;

    return isExpiringSoon || isExpired;
  }
  return false;
};

export { isUserPlannedForTraining, isUserPlannedFor, isPlanned };
